/**
 * EnergyChartBase - shared logic for Style01/02
 * Range dropdown + energy sensor switch (watt, watt_hour, voltage)
 * Style01: AreaChart, Style02: BarChart
 */
import { useState, useMemo, useEffect } from 'react'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import {
  getEnergySensors,
  MOCK_ENERGY_SENSOR_OPTIONS,
  type EnergySensorId,
} from './energySensorConfig'
import { CHARTABLE_SENSOR_CONFIG } from './chartableSensorConfig'
import type { ChartableSensorOption } from './chartableSensorConfig'
import './EnergyChart.css'

export type TimeRange = '1H' | '24H' | '7D' | '30D'

const TIME_RANGES: { id: TimeRange; label: string }[] = [
  { id: '1H', label: '1H' },
  { id: '24H', label: '24H' },
  { id: '7D', label: '7D' },
  { id: '30D', label: '30D' },
]

function generateMockEnergyData(
  range: TimeRange,
  sensorId: EnergySensorId
): Array<{ time: string; value: number }> {
  const cfg = CHARTABLE_SENSOR_CONFIG[sensorId]
  if (!cfg) return []

  const now = Date.now()
  const points = range === '1H' ? 12 : range === '24H' ? 24 : range === '7D' ? 7 : 30
  const intervalMs =
    range === '1H'
      ? 5 * 60 * 1000
      : range === '24H'
        ? 60 * 60 * 1000
        : range === '7D'
          ? 24 * 60 * 60 * 1000
          : 24 * 60 * 60 * 1000

  const data: Array<{ time: string; value: number }> = []
  let base = cfg.base
  for (let i = points; i >= 0; i--) {
    const t = new Date(now - i * intervalMs)
    base += (Math.random() - 0.5) * 2
    base = Math.max(cfg.min, Math.min(cfg.max, base))
    const value = Math.round(base * 10) / 10
    const timeStr =
      range === '1H'
        ? t.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        : range === '24H'
          ? t.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          : t.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })
    data.push({ time: timeStr, value })
  }
  return data
}

export type EnergyChartVariant = 'blue' | 'gold'
export type EnergyChartType = 'area' | 'bar'

export interface EnergyChartBaseProps {
  variant: EnergyChartVariant
  chartType: EnergyChartType
  deviceSN?: string | null
  credentials?: { apiKey?: string; appId?: string }
}

const VARIANT_STYLES: Record<
  EnergyChartVariant,
  {
    stroke: string
    fill: string
    gradientId: string
    gradientFrom: string
    gradientTo: string
    dropdownActiveClass: string
  }
> = {
  blue: {
    stroke: '#38bdf8',
    fill: '#38bdf8',
    gradientId: 'energyGradient-blue',
    gradientFrom: '#38bdf8',
    gradientTo: '#38bdf8',
    dropdownActiveClass: 'energy-chart__dropdown--blue',
  },
  gold: {
    stroke: '#fbbf24',
    fill: '#fbbf24',
    gradientId: 'energyGradient-gold',
    gradientFrom: '#fbbf24',
    gradientTo: '#fbbf24',
    dropdownActiveClass: 'energy-chart__dropdown--gold',
  },
}

export function EnergyChartBase({ variant, chartType, deviceSN, credentials }: EnergyChartBaseProps) {
  const [range, setRange] = useState<TimeRange>('24H')
  const [sensorOptions, setSensorOptions] = useState<ChartableSensorOption[]>(MOCK_ENERGY_SENSOR_OPTIONS)
  const [sensor, setSensor] = useState<EnergySensorId>('watt')
  const [loading, setLoading] = useState(!!(deviceSN?.trim() && credentials?.apiKey))

  useEffect(() => {
    let cancelled = false
    if (deviceSN?.trim() && credentials?.apiKey) setLoading(true)
    getEnergySensors(deviceSN, credentials)
      .then((opts) => {
        if (!cancelled) {
          setSensorOptions(opts)
          setSensor((prev) => {
            const has = opts.some((o) => o.id === prev)
            return has ? prev : ((opts[0]?.id as EnergySensorId) ?? 'watt')
          })
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [deviceSN, credentials?.apiKey, credentials?.appId])

  const data = useMemo(() => generateMockEnergyData(range, sensor), [range, sensor])
  const styles = VARIANT_STYLES[variant]
  const cfg = CHARTABLE_SENSOR_CONFIG[sensor]

  const stats = useMemo(() => {
    const vals = data.map((d) => d.value)
    const current = vals[vals.length - 1] ?? 0
    const max = Math.max(...vals)
    const min = Math.min(...vals)
    return { current, max, min }
  }, [data])

  const formatY = (v: number) => `${v}`
  const formatTooltip = (value: number | string): [string, string] => {
    const numeric = typeof value === 'number' ? value : Number(value || 0)
    return cfg ? [`${numeric}${cfg.unit}`, cfg.label] : ['', '']
  }

  if (loading || sensorOptions.length === 0) {
    return (
      <div className={`energy-chart energy-chart--${variant}`}>
        <div className="energy-chart__loading">Loading sensors...</div>
      </div>
    )
  }

  const chartMargin = { top: 8, right: 8, left: 8, bottom: 8 }
  const axisStyle = {
    tick: { fill: 'rgba(148,163,184,0.8)', fontSize: 10 },
    axisLine: { stroke: 'rgba(255,255,255,0.2)' },
    tickLine: { stroke: 'rgba(255,255,255,0.2)' },
  }
  const tooltipStyle = {
    contentStyle: {
      background: 'rgba(15,23,42,0.95)',
      border: '1px solid rgba(255,255,255,0.2)',
      borderRadius: 8,
    },
    labelStyle: { color: '#94a3b8' },
  }

  return (
    <div className={`energy-chart energy-chart--${variant}`}>
      <div className="energy-chart__controls">
        <div className={`energy-chart__dropdown ${styles.dropdownActiveClass}`}>
          <span className="energy-chart__dropdown-icon material-symbols-rounded" aria-hidden>schedule</span>
          <select
            id={`ec-range-${variant}`}
            value={range}
            onChange={(e) => setRange(e.target.value as TimeRange)}
            className="energy-chart__select"
            aria-label="Time range"
          >
            {TIME_RANGES.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div className={`energy-chart__dropdown ${styles.dropdownActiveClass}`}>
          <span className="energy-chart__dropdown-icon material-symbols-rounded" aria-hidden>electric_bolt</span>
          <select
            id={`ec-sensor-${variant}`}
            value={sensor}
            onChange={(e) => setSensor(e.target.value as EnergySensorId)}
            className="energy-chart__select"
            aria-label="Energy sensor type"
          >
            {sensorOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div
        className="energy-chart__chart"
        data-css-slot="chartLine"
        data-css-slot-label="Chart Line"
      >
        <ResponsiveContainer width="100%" height={180}>
          {chartType === 'area' ? (
            <AreaChart data={data} margin={chartMargin}>
              <defs>
                <linearGradient id={styles.gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={styles.gradientFrom} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={styles.gradientTo} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="time" {...axisStyle} />
              <YAxis domain={['dataMin - 2', 'dataMax + 2']} {...axisStyle} tickFormatter={formatY} />
              <Tooltip {...tooltipStyle} formatter={(value) => formatTooltip(value)} />
              <Area
                type="monotone"
                dataKey="value"
                stroke={styles.stroke}
                strokeWidth={2}
                fill={`url(#${styles.gradientId})`}
              />
            </AreaChart>
          ) : (
            <BarChart data={data} margin={chartMargin}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="time" {...axisStyle} />
              <YAxis domain={['dataMin - 2', 'dataMax + 2']} {...axisStyle} tickFormatter={formatY} />
              <Tooltip
                {...tooltipStyle}
                cursor={{ fill: 'transparent', stroke: styles.stroke, strokeWidth: 2 }}
                formatter={(value) => formatTooltip(value)}
              />
              <Bar dataKey="value" fill={styles.fill} radius={[4, 4, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      <div className="energy-chart__stats">
        <span>Now {stats.current}{cfg.unit}</span>
        <span>Max {stats.max}{cfg.unit}</span>
        <span>Min {stats.min}{cfg.unit}</span>
      </div>
    </div>
  )
}
